<template>
  <div class="history-sidebar">
    <div class="sidebar-header">
      <button class="new-chat-btn" @click="createNewChat">
        <span>+ New Chat</span>
      </button>
    </div>
    <div class="conversations-list">
      <div
        v-for="conv in conversations"
        :key="conv.id"
        class="conversation-item"
        :class="{ active: conv.id === activeConversationId }"
        @click="selectConversation(conv.id)"
      >
        <div class="conversation-header">
          <p class="conv-title">{{ getConversationTitle(conv) }}</p>
          <button 
            class="delete-btn"
            @click.stop="confirmDelete(conv.id)"
            title="Delete conversation"
          >
            ×
          </button>
        </div>
        <span class="conv-date">{{ formatDate(conv.created_at) }}</span>
      </div>
      <div v-if="conversations.length === 0" class="empty-state">
        No conversations yet
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="confirmation-modal">
      <div class="modal-overlay" @click="showDeleteConfirm = false"></div>
      <div class="modal-content">
        <h3>Delete Conversation</h3>
        <p>
          Are you sure you want to delete this conversation? This action cannot be undone.
        </p>
        <div class="modal-actions">
          <button class="btn-cancel" @click="showDeleteConfirm = false">
            Cancel
          </button>
          <button class="btn-delete" @click="deleteConversation">
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiClient } from "@/plugins/apiService.js";

export default {
  name: "ConversationHistory",
  props: {
    activeConversationId: {
      type: Number,
      default: null,
    },
    conversations: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["create-chat", "select-conversation", "delete-conversation"],
  data() {
    return {
      showDeleteConfirm: false,
      conversationToDelete: null,
    };
  },
  methods: {
    createNewChat() {
      this.$emit("create-chat");
    },

    selectConversation(conversationId) {
      this.$emit("select-conversation", conversationId);
    },

    getConversationTitle(conversation) {
      // Use problem statement if available, otherwise use date
      if (conversation.problem_statement) {
        return conversation.problem_statement;
      }
      const timestamp = new Date(conversation.created_at);
      const date = timestamp.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });
      return `Chat • ${date}`;
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    },

    confirmDelete(conversationId) {
      this.conversationToDelete = conversationId;
      this.showDeleteConfirm = true;
    },

    async deleteConversation() {
      if (!this.conversationToDelete) return;

      try {
        await apiClient.delete(`/chat/${this.conversationToDelete}`);
        
        // Emit delete event to parent
        this.$emit("delete-conversation", this.conversationToDelete);
        
        // Close modal
        this.showDeleteConfirm = false;
        this.conversationToDelete = null;
      } catch (error) {
        console.error("Error deleting conversation:", error);
        alert("Failed to delete conversation. Please try again.");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.history-sidebar {
  flex: 0 0 260px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e5e5;
  overflow: hidden;

  .sidebar-header {
    padding: 16px 12px;
    border-bottom: 1px solid #e5e5e5;

    .new-chat-btn {
      width: 100%;
      padding: 10px 12px;
      background-color: white;
      border: 1px solid #d9d9d9;
      border-radius: 8px;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--darkColor);
      transition: all 0.2s ease;

      &:hover {
        background-color: var(--primaryColor);
        color: white;
        border-color: var(--primaryColor);
      }
    }
  }

  .conversations-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--secondaryColorLight);
      border-radius: 3px;

      &:hover {
        background: #999;
      }
    }
  }

  .empty-state {
    padding: 20px 12px;
    text-align: center;
    color: #999;
    font-size: 0.875rem;
  }

  .conversation-item {
    padding: 12px;
    margin-bottom: 4px;
    background-color: transparent;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;

    &:hover {
      background-color: #f0f0f0;
    }

    &.active {
      background-color: var(--primaryColor);
      border-color: var(--primaryColor);

      .conv-title,
      .conv-date {
        color: white;
      }

      .delete-btn {
        color: white;
      }
    }

    .conversation-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 8px;
    }

    .conv-title {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--darkColor);
      flex: 1;
      word-break: break-word;
    }

    .delete-btn {
      color: #999;
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        color: #d32f2f;
      }
    }

    .conv-date {
      display: block;
      font-size: 0.75rem;
      color: #999;
      margin-top: 4px;
    }
  }
}

/* Confirmation Modal Styles */
.confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
  }

  .modal-content {
    position: relative;
    background-color: white;
    border-radius: 12px;
    padding: 24px;
    max-width: 400px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

    h3 {
      margin: 0 0 12px 0;
      font-size: 1.1rem;
      color: var(--darkColor);
    }

    p {
      margin: 0 0 20px 0;
      color: #666;
      font-size: 0.95rem;
      line-height: 1.5;
    }

    .modal-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;

      button {
        padding: 10px 16px;
        border: none;
        border-radius: 6px;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .btn-cancel {
        background-color: #f0f0f0;
        color: var(--darkColor);

        &:hover {
          background-color: #e0e0e0;
        }
      }

      .btn-delete {
        background-color: #d32f2f;
        color: white;

        &:hover {
          background-color: #b71c1c;
        }
      }
    }
  }
}

@media (max-width: 1024px) {
  .history-sidebar {
    flex: 0 0 50px;
    border-right: none;
    border-bottom: 1px solid #e5e5e5;

    .sidebar-header {
      padding: 8px 12px;
    }

    .conversations-list {
      display: none;
    }

    .new-chat-btn {
      width: 32px;
      height: 32px;
      padding: 0;
      font-size: 0;

      span {
        display: none;
      }

      &::before {
        content: "+";
        font-size: 18px;
      }
    }
  }
}
</style>
